import React, { useState, useEffect, useRef } from "react";
import { chatService } from "../../services/chatService";
import { databaseChatService } from "../../services/databaseChatService";
import { detectLanguage } from "../../services/languageDetector";
import { contentService } from "../../services/contentService";
import "./ChatInterface.css";
import "./Chatsidebar.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import rehypeSanitize from "rehype-sanitize";
import { defaultSchema } from "rehype-sanitize";
import { PrismLight as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";

// Language imports for syntax highlighting
import python from "react-syntax-highlighter/dist/esm/languages/prism/python";
import javascript from "react-syntax-highlighter/dist/esm/languages/prism/javascript";
import bash from "react-syntax-highlighter/dist/esm/languages/prism/bash";
import json from "react-syntax-highlighter/dist/esm/languages/prism/json";
import sql from "react-syntax-highlighter/dist/esm/languages/prism/sql";
import yaml from "react-syntax-highlighter/dist/esm/languages/prism/yaml";
import java from "react-syntax-highlighter/dist/esm/languages/prism/java";
import html from "react-syntax-highlighter/dist/esm/languages/prism/markup";

SyntaxHighlighter.registerLanguage("python", python);
SyntaxHighlighter.registerLanguage("javascript", javascript);
SyntaxHighlighter.registerLanguage("bash", bash);
SyntaxHighlighter.registerLanguage("json", json);
SyntaxHighlighter.registerLanguage("sql", sql);
SyntaxHighlighter.registerLanguage("yaml", yaml);
SyntaxHighlighter.registerLanguage("java", java);
SyntaxHighlighter.registerLanguage("html", html);

const ChatInterface = ({ isOpen, onClose }) => {
  const [messages, setMessages] = useState([
    { text: contentService.getChatConfig().welcome_message, sender: "bot" },
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [currentTyping, setCurrentTyping] = useState("");
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [chatSessions, setChatSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMode, setSearchMode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const chatMessagesRef = useRef(null);
  const chatInputRef = useRef(null);

  useEffect(() => {
    const chatInterface = document.querySelector(".chat-interface");
    if (isOpen) {
      chatInterface.style.display = "flex";
      setTimeout(() => {
        chatInterface.classList.add("open");
        chatInputRef.current?.focus();
      }, 10);
    } else {
      chatInterface.classList.remove("open");
      setIsFullscreen(false);
      setTimeout(() => (chatInterface.style.display = "none"), 300);
    }
  }, [isOpen]);

  useEffect(() => {
    const chatInterface = document.querySelector(".chat-interface");
    if (!chatInterface) return;
    
    if (isFullscreen) {
      chatInterface.classList.add("fullscreen");
    } else {
      chatInterface.classList.remove("fullscreen");
      if (isOpen) {
        chatInterface.style.display = "flex";
        chatInterface.classList.add("open");
      }
    }
  }, [isFullscreen, isOpen]);

  useEffect(() => {
    loadSessions();
    
    // Initialize with a new chat session if none exists
    if (!currentSessionId) {
      const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setCurrentSessionId(newSessionId);
    }
  }, []);

  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [messages, currentTyping, isTyping]);

  // ✨ Clean AI response with HTML entity decoding while preserving colors
  const cleanBotText = (rawText, lastUserText = "") => {
    if (!rawText) return "";
    let text = rawText;

    // First decode double-encoded entities (like &amp;lt; -> &lt; -> <)
    text = text.replace(/&amp;lt;/g, '<')
                .replace(/&amp;gt;/g, '>')
                .replace(/&amp;quot;/g, '"')
                .replace(/&amp;#39;/g, "'")
                .replace(/&amp;nbsp;/g, ' ');

    // Then decode remaining HTML entities
    text = text.replace(/&lt;/g, '<')
                .replace(/&gt;/g, '>')
                .replace(/&quot;/g, '"')
                .replace(/&#39;/g, "'")
                .replace(/&#x27;/g, "'")
                .replace(/&#x60;/g, '`')
                .replace(/&nbsp;/g, ' ')
                .replace(/&amp;/g, '&'); // This should be last

    if (lastUserText) {
      const escaped = lastUserText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const echoRegex = new RegExp(`^\\s*${escaped}[\\s\\:\\-\\—\\–\\?\\!\\.]*\\s*`, "i");
      text = text.replace(echoRegex, "");
    }

    return text.replace(/\r\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  };

  // 💡 Smart code block detection with automatic language detection
  const detectCodeBlocks = (text) => {
    if (text.includes('```')) {
      return text;
    }
    
    const paragraphs = text.split('\n\n');
    let result = [];
    
    paragraphs.forEach(paragraph => {
      const lines = paragraph.split('\n');
      const nonEmptyLines = lines.filter(line => line.trim());
      
      // Check if this paragraph looks like code
      const codeIndicators = nonEmptyLines.filter(line => {
        const trimmed = line.trim();
        return trimmed && (
          /^(def|class|import|from|if|for|while|try|except|return)\s/.test(trimmed) ||
          /^(function|const|let|var|if|for|while|try|catch|return)\s/.test(trimmed) ||
          /^[a-zA-Z_][a-zA-Z0-9_]*\s*\(.*\)\s*:?\s*$/.test(trimmed) ||
          /^\s*(if|for|while)\s+.*:\s*$/.test(trimmed) ||
          /^\s+/.test(line) && trimmed.length > 0
        );
      });
      
      if (nonEmptyLines.length >= 2 && codeIndicators.length / nonEmptyLines.length > 0.6) {
        // Use automatic language detection
        const detectedLanguage = detectLanguage(paragraph);
        result.push(`\`\`\`${detectedLanguage}\n${paragraph}\n\`\`\``);
      } else {
        result.push(paragraph);
      }
    });
    
    return result.join('\n\n');
  };

  const addMessage = (text, sender) => {
    setMessages((prev) => {
      const newMessages = [...prev, { text, sender }];
      saveCurrentSession(newMessages);
      return newMessages;
    });
  };

  const loadSessions = async () => {
    const sessions = await databaseChatService.getSessions();
    setChatSessions(sessions);
  };

  const saveCurrentSession = async (msgs = messages) => {
    if (!currentSessionId || msgs.length <= 1) return;
    // Sessions are saved automatically via API
  };

  const createNewChat = () => {
    const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setCurrentSessionId(newSessionId);
    setMessages([{ text: contentService.getChatConfig().welcome_message, sender: "bot" }]);
    setSidebarOpen(false);
  };

  const loadChatSession = (session) => {
    setCurrentSessionId(session.id);
    setMessages(session.messages);
    setSidebarOpen(false);
  };

  const deleteChatSession = async (sessionId, e) => {
    e.stopPropagation();
    const success = await databaseChatService.deleteSession(sessionId);
    if (success) {
      if (currentSessionId === sessionId) {
        createNewChat();
      }
      loadSessions();
    }
  };

  const filteredSessions = searchQuery 
    ? chatSessions.filter(session => 
        session.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        session.messages.some(msg => msg.text.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : chatSessions;

  // ✍️ Simulate typing animation
  const simulateTyping = async (fullText) => {
    setCurrentTyping("");
    setIsTyping(true);
    const words = fullText.split(/(\s+)/);
    for (let i = 0; i < words.length; i++) {
      await new Promise((r) => setTimeout(r, 15));
      setCurrentTyping((prev) => prev + words[i]);
    }
    setIsTyping(false);
    setCurrentTyping("");
    
    // Add bot message and save session
    setMessages((prev) => {
      const newMessages = [...prev, { text: fullText, sender: "bot" }];
      saveCurrentSession(newMessages);
      return newMessages;
    });
  };

  const handleSendMessage = async () => {
    const message = inputValue.trim();
    if (!message) return;
    
    // Add user message
    const newMessagesWithUser = [...messages, { text: message, sender: "user" }];
    setMessages(newMessagesWithUser);
    
    setInputValue("");
    chatInputRef.current.style.height = "auto";
    setIsWaitingForResponse(true);

    try {
      console.log('Sending conversation history:', newMessagesWithUser);
      const response = await databaseChatService.sendMessage(message, currentSessionId, newMessagesWithUser);
      let rawReply = response?.message || response?.text || "";
      console.log('Raw response:', rawReply);
      const cleaned = cleanBotText(rawReply, message);
      console.log('Cleaned response:', cleaned);
      const formatted = detectCodeBlocks(cleaned);
      console.log('Formatted response:', formatted);
      setIsWaitingForResponse(false);
      await simulateTyping(formatted);
      loadSessions(); // Refresh sessions after saving
    } catch (err) {
      console.error("Chat error:", err);
      setIsWaitingForResponse(false);
      await simulateTyping("⚠️ Sorry, something went wrong. Please try again.");
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !isTyping) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 100) + "px";
  };

  // Copy-to-Clipboard feature for code blocks and messages
  const CopyButton = ({ code, isMessageCopy = false }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      } catch (err) {
        console.error("Copy failed:", err);
      }
    };

    return (
      <button className={`copy-btn ${isMessageCopy ? 'message-copy-btn' : ''}`} onClick={handleCopy}>
        <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`}></i>
        {!isMessageCopy && (copied ? "Copied!" : "Copy")}
      </button>
    );
  };

  // Custom code block component with copy functionality and auto-detection
  const CodeBlock = ({ node, inline, className, children, ...props }) => {
    const match = /language-(\w+)/.exec(className || '');
    const code = String(children).replace(/\n$/, '');
    
    // Auto-detect language if not specified
    const language = match ? match[1] : detectLanguage(code);

    // Render as block code if NOT inline AND (has language class OR contains newlines)
    if (!inline && (match || code.includes('\n'))) {
      return (
        <div className="code-block-container">
          <div className="code-header">
            <span className="language-label">{language}</span>
            <CopyButton code={code} />
          </div>
          <SyntaxHighlighter
            style={oneDark}
            language={language}
            PreTag="div"
            showLineNumbers={code.split('\n').length > 1}
            customStyle={{
              margin: 0,
              borderRadius: '0 0 8px 8px',
              fontSize: '0.85rem'
            }}
            {...props}
          >
            {code}
          </SyntaxHighlighter>
        </div>
      );
    }
    
    // Inline code - let CSS handle the styling
    return (
      <code className="inline-code" {...props}>
        {children}
      </code>
    );
  };

  // 🧩 Markdown renderers (with syntax highlighting + copy)
  const renderers = {
    code: CodeBlock,
    table({ children, ...props }) {
      return (
        <div style={{ overflowX: "auto", margin: "16px 0" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid #e5e7eb",
              borderRadius: "8px",
              overflow: "hidden",
            }}
            {...props}
          >
            {children}
          </table>
        </div>
      );
    },
    thead({ children, ...props }) {
      return (
        <thead
          style={{
            backgroundColor: "#f9fafb",
            fontWeight: "600",
          }}
          {...props}
        >
          {children}
        </thead>
      );
    },
    th({ children, ...props }) {
      return (
        <th
          style={{
            padding: "12px 16px",
            textAlign: "left",
            borderBottom: "2px solid #e5e7eb",
            fontSize: "0.875rem",
            color: "#374151",
          }}
          {...props}
        >
          {children}
        </th>
      );
    },
    td({ children, ...props }) {
      return (
        <td
          style={{
            padding: "12px 16px",
            borderBottom: "1px solid #f3f4f6",
            fontSize: "0.875rem",
          }}
          {...props}
        >
          {children}
        </td>
      );
    },
    h3({ children, ...props }) {
      return (
        <h3
          style={{
            fontSize: "1.125rem",
            fontWeight: "600",
            margin: "24px 0 12px 0",
            color: "#1f2937",
            borderBottom: "2px solid #e5e7eb",
            paddingBottom: "8px",
          }}
          {...props}
        >
          {children}
        </h3>
      );
    },
  };

  return (
    <div className="chat-interface" id="chatInterface">
      {sidebarOpen && (
        <div className="chat-sidebar">
        <div className="sidebar-header">
            <button className="new-chat-btn" onClick={createNewChat}>
              <div className="flex items-center justify-center group-disabled:opacity-50 group-data-disabled:opacity-50 icon">
                <i className="fas fa-plus"></i>
              </div>
              {contentService.getChatConfig().new_chat_text}
            </button>
            {!searchMode ? (
              <button className="search-chats-btn" onClick={() => setSearchMode(true)}>
                <div className="flex items-center justify-center group-disabled:opacity-50 group-data-disabled:opacity-50 icon">
                  <i className="fas fa-search"></i>
                </div>
                {contentService.getChatConfig().search_placeholder.replace('...', '')}
              </button>
            ) : (
              <div className="search-container">
                <input
                  type="text"
                  className="search-input"
                  placeholder={contentService.getChatConfig().search_placeholder}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onBlur={() => !searchQuery && setSearchMode(false)}
                  autoFocus
                />
                <button className="search-close" onClick={() => { setSearchQuery(""); setSearchMode(false); }}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}
        </div>
        <div className="chat-history">
            {chatSessions.length > 0 && (
              <div className="history-title">{contentService.getChatConfig().history_title}</div>
            )}
            {filteredSessions.length === 0 ? (
              <div className="empty">
                {searchQuery ? contentService.getChatConfig().no_matches : contentService.getChatConfig().no_chats}
              </div>
            ) : (
              filteredSessions.map((session) => (
                <div
                  key={session.id}
                  className={`chat-item ${currentSessionId === session.id ? 'active' : ''}`}
                  onClick={() => loadChatSession(session)}
                  title={`Session ID: ${session.id}`}
                >
                  <div className="chat-item-content">
                    <span className="chat-title">{session.title}</span>
                    <span className="chat-date">
                      {new Date(session.timestamp).toLocaleDateString()}
                    </span>
                  </div>
                  <button 
                    className="delete-chat-btn" 
                    onClick={(e) => deleteChatSession(session.id, e)}
                    title="Delete chat"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              ))
            )}
        </div>
        </div>
      )}
      
      <div className="chat-main">
        <div className="chat-header">
          <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <i className="fas fa-bars"></i>
          </button>
          <div className="chat-title">{contentService.getChatConfig().assistant_name}</div>
          <div className="header-controls">
            <button className="fullscreen-toggle" onClick={() => setIsFullscreen(!isFullscreen)}>
              <i className={`fas ${isFullscreen ? 'fa-compress' : 'fa-expand'}`}></i>
            </button>
            <button className="close-chat" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div className="chat-messages" ref={chatMessagesRef}>
        {messages.map((message, index) => (
          <div key={index} className={`message ${message.sender}`}>
            {message.sender === "bot" ? (
              <div className="bot-message-wrapper">
                <div className="bot-message-content">
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]} 
                    rehypePlugins={[
                      rehypeRaw, 
                      [rehypeSanitize, {
                        ...defaultSchema,
                        attributes: {
                          ...defaultSchema.attributes,
                          span: ['style', 'className'],
                          u: [],
                          '*': ['style']
                        }
                      }]
                    ]}
                    components={renderers}
                    skipHtml={false}
                  >
                    {message.text}
                  </ReactMarkdown>
                </div>
                <CopyButton code={message.text} isMessageCopy={true} />
              </div>
            ) : (
              <div style={{ whiteSpace: "pre-wrap" }}>{message.text}</div>
            )}
          </div>
        ))}

        {isWaitingForResponse && (
          <div className="message bot thinking-message">
            <div className="thinking-indicator">
              <span>{contentService.getChatConfig().thinking_text}</span>
              <div className="thinking-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        {isTyping && currentTyping && (
          <div className="message bot typing-text">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]} 
              rehypePlugins={[
                rehypeRaw, 
                [rehypeSanitize, {
                  ...defaultSchema,
                  attributes: {
                    ...defaultSchema.attributes,
                    span: ['style', 'className'],
                    u: [],
                    '*': ['style']
                  }
                }]
              ]}
              components={renderers}
            >
              {currentTyping}
            </ReactMarkdown>
          </div>
        )}
        </div>

        <div className="chat-input-container">
        <textarea
          ref={chatInputRef}
          className="chat-input"
          placeholder={isTyping || isWaitingForResponse ? "Bot is responding..." : "Type your message..."}
          rows="1"
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          disabled={isTyping || isWaitingForResponse}
        />
        <button className="send-button" onClick={handleSendMessage} disabled={isTyping || isWaitingForResponse}>
          <i className="fas fa-paper-plane"></i>
        </button>
        </div>
      </div>
    </div>
  );
};

export default ChatInterface;
